import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, ArrowRight, ArrowLeft, CheckCircle2, XCircle, RotateCcw, Briefcase } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const PROFESSIONS = [
  "Registered Nurse", "Phlebotomist", "EMT / Paramedic", "School Nurse",
  "Dental Hygienist", "Tattoo Artist", "Piercing Artist", "Custodial Staff",
  "Lab Technician", "Firefighter", "Correctional Officer",
  "Esthetician", "Spa Therapist", "Semi-Permanent Makeup Artist",
  "Salon Assistant", "Homemaker", "Sports Teacher", "Fitness Trainer",
  "Sports Coach", "Massage Therapist", "Childcare Worker",
];

export default function PracticeCases() {
  const [step, setStep] = useState("profession"); // profession | loading | practice | done
  const [profession, setProfession] = useState("");
  const [cases, setCases] = useState([]);
  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [score, setScore] = useState(0);

  const generate = async () => {
    const p = profession.trim();
    if (!p) return toast.error("Please enter your profession");
    setStep("loading");
    try {
      // Two parallel batches of 5 to avoid ingress timeout on a single big LLM call.
      const [r1, r2] = await Promise.all([
        api.post("/practice/generate", { profession: p, batch: 1 }),
        api.post("/practice/generate", { profession: p, batch: 2 }),
      ]);
      const combined = [...(r1.data.cases || []), ...(r2.data.cases || [])];
      if (combined.length === 0) throw new Error("no cases returned");
      setCases(combined);
      setIdx(0); setSelected(null); setRevealed(false); setScore(0);
      setStep("practice");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not generate cases");
      setStep("profession");
    }
  };

  const reveal = () => {
    if (selected == null) return toast.error("Select an option first");
    if (selected === cases[idx].correct_index) setScore((s) => s + 1);
    setRevealed(true);
  };

  const next = () => {
    if (idx + 1 >= cases.length) {
      // Persist score to dashboard (fire-and-forget — don't block the UI)
      const finalScore = score + (selected === cases[idx].correct_index && revealed ? 0 : 0);
      api.post("/practice/save-score", {
        profession: profession.trim(),
        score: finalScore,
        total: cases.length,
      }).catch((err) => { console.error("Save practice score failed", err); });
      return setStep("done");
    }
    setIdx(idx + 1); setSelected(null); setRevealed(false);
  };

  const restart = () => {
    setStep("profession"); setCases([]); setIdx(0);
    setSelected(null); setRevealed(false); setScore(0);
  };

  if (step === "profession" || step === "loading") {
    return (
      <div className="bg-white border border-zinc-200 rounded-lg p-8 lg:p-12" data-testid="practice-profession-step">
        <div className="flex items-center gap-3 mb-4">
          <Briefcase className="h-6 w-6 text-red-600" />
          <div className="text-xs font-mono uppercase tracking-widest text-red-600">Practice module</div>
        </div>
        <h1 className="font-display text-4xl font-bold tracking-tight">What's your profession?</h1>
        <p className="text-zinc-600 mt-3 max-w-xl">
          Claude Sonnet 4.5 will generate <strong>10 tailored practice case studies</strong> with 4-choice responses.
          These do NOT count toward your certification exam — pure practice.
        </p>

        <div className="mt-8 max-w-md">
          <Input
            value={profession}
            onChange={(e) => setProfession(e.target.value)}
            placeholder="e.g. Esthetician, Registered Nurse, EMT…"
            maxLength={120}
            disabled={step === "loading"}
            className="h-12 text-base"
            data-testid="practice-profession-input"
            onKeyDown={(e) => e.key === "Enter" && step !== "loading" && generate()}
          />
        </div>

        <div className="mt-5">
          <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">Quick picks</div>
          <div className="flex flex-wrap gap-2" data-testid="practice-suggestions">
            {PROFESSIONS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setProfession(p)}
                disabled={step === "loading"}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                  profession === p ? "border-red-600 bg-red-50 text-red-800 font-medium" : "border-zinc-300 bg-white hover:border-zinc-500"
                }`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-10">
          <Button
            onClick={generate}
            disabled={!profession.trim() || step === "loading"}
            className="bg-red-600 hover:bg-red-700 text-white disabled:bg-zinc-300"
            data-testid="practice-generate-btn"
          >
            {step === "loading" ? (
              <><Sparkles className="mr-2 h-4 w-4 animate-pulse" /> Generating 10 practice cases…</>
            ) : (
              <>Generate 10 practice cases <ArrowRight className="ml-2 h-4 w-4" /></>
            )}
          </Button>
        </div>
      </div>
    );
  }

  if (step === "done") {
    return (
      <div className="bg-white border-2 border-zinc-900 rounded-lg p-10 fade-in text-center" data-testid="practice-done-step">
        <CheckCircle2 className="h-14 w-14 text-red-600 mx-auto mb-4" strokeWidth={1.5} />
        <div className="text-xs font-mono uppercase tracking-widest text-red-600">Practice complete</div>
        <h1 className="font-display text-4xl font-black tracking-tight mt-3">You scored {score} of {cases.length}</h1>
        <p className="text-zinc-600 mt-3 max-w-lg mx-auto">
          Great work! Practice as often as you want — a different set of scenarios is generated every time.
        </p>
        <div className="mt-8 flex gap-3 justify-center">
          <Button onClick={restart} className="bg-red-600 hover:bg-red-700 text-white" data-testid="practice-restart-btn">
            <RotateCcw className="mr-2 h-4 w-4" /> Practice again
          </Button>
        </div>
      </div>
    );
  }

  const c = cases[idx];
  const chosenCorrect = revealed && selected === c.correct_index;

  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-8 lg:p-12 fade-in" data-testid="practice-case-step">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs font-mono uppercase tracking-widest text-red-600">
          Case {idx + 1} of {cases.length}
        </div>
        <div className="text-xs font-mono text-zinc-500">Score: {score}/{idx + (revealed ? 1 : 0)}</div>
      </div>
      <h2 className="font-display text-2xl lg:text-3xl font-bold tracking-tight leading-tight">{c.title}</h2>

      <div className="mt-5 border-l-4 border-red-600 bg-red-50 rounded-r-md p-5">
        <p className="text-zinc-800 leading-relaxed text-sm">{c.scenario}</p>
      </div>

      <div className="mt-6 space-y-3">
        {c.options.map((opt, i) => {
          const isCorrect = i === c.correct_index;
          const isChosen = selected === i;
          let cls = "border-zinc-200 hover:border-zinc-400 bg-white";
          if (revealed) {
            if (isCorrect) cls = "border-emerald-600 bg-emerald-50";
            else if (isChosen) cls = "border-red-600 bg-red-50";
          } else if (isChosen) {
            cls = "border-red-600 bg-red-50";
          }
          return (
            <button
              key={`opt-${idx}-${i}`}
              onClick={() => !revealed && setSelected(i)}
              disabled={revealed}
              className={`w-full text-left border-2 rounded-md p-4 transition-colors ${cls}`}
              data-testid={`practice-option-${i}`}
            >
              <div className="flex items-start gap-3 text-sm">
                <span className="font-mono text-xs text-zinc-500 pt-0.5">{String.fromCharCode(65 + i)}.</span>
                <span className="flex-1 text-zinc-800">{opt}</span>
                {revealed && isCorrect && <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0" />}
                {revealed && isChosen && !isCorrect && <XCircle className="h-4 w-4 text-red-600 flex-shrink-0" />}
              </div>
            </button>
          );
        })}
      </div>

      {revealed && (
        <div className={`mt-5 border-l-4 p-4 rounded-r-md ${chosenCorrect ? "border-emerald-600 bg-emerald-50" : "border-amber-600 bg-amber-50"}`} data-testid="practice-explanation">
          <div className="text-xs font-mono uppercase tracking-widest mb-1">
            {chosenCorrect ? "Correct" : "Not quite"}
          </div>
          <p className="text-sm text-zinc-800 leading-relaxed">{c.explanation}</p>
        </div>
      )}

      <div className="mt-8 flex justify-between">
        <Button variant="outline" onClick={restart} data-testid="practice-restart-btn-inline">
          <ArrowLeft className="mr-2 h-4 w-4" /> Start over
        </Button>
        {!revealed ? (
          <Button onClick={reveal} disabled={selected == null} className="bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="practice-reveal-btn">
            Check answer
          </Button>
        ) : (
          <Button onClick={next} className="bg-red-600 hover:bg-red-700 text-white" data-testid="practice-next-btn">
            {idx + 1 >= cases.length ? "Finish" : "Next case"} <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
